import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Divider,
  Button,
  Collapse
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDroplet, faBoltLightning } from '@fortawesome/pro-solid-svg-icons';
import clsx from "clsx";
import { getDateTimeFromAPIDate, getDateToString } from "src/utils/date";
import {
  VolumeNumberFormatString as formatVolume,
  EnergyNumberFormatString as formatEnergy,
} from 'src/utils/numbers';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    fontSize: "1.1rem"
  },
  messageText: {
    fontSize: "0.95rem",
  },
  valueText: {
    fontSize: "0.9rem",
  },
  alertBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  }
}));

function ConsumptionAlertsCard({ alerts, className, ...rest }) {
  const classes = useStyles();
  const [openAlerts, setOpenAlerts] = useState({});

  if (!alerts || alerts.length === 0) return null;

  const formatters = {
    volume: formatVolume,
    energy: formatEnergy,
    empty: () => ''
  };

  const rows = [
    { field: "ColdWater", text: "Kallvatten", icon: faDroplet, formatter: 'volume', iconColor: "#0289d1" },
    { field: "HotWater", text: "Varmvatten", icon: faDroplet, formatter: 'volume', iconColor: "red" },
    { field: "Electricity", text: "El", icon: faBoltLightning, formatter: 'energy', iconColor: "orange" }
  ];

  return (
    <Card className={clsx(classes.root, className)} {...rest} sx={{ mt: 2 }}>
      <Typography component="h3" gutterBottom variant="overline" color="textSecondary">
        Förbrukningsvarningar
      </Typography>

      {rows.map(row => {
        const typeAlerts = alerts.filter(a => a.type === row.field);
        if (!typeAlerts.length) return null;

        return (
          <Box key={row.field}>
            <Typography className={classes.sectionTitle}>
              <FontAwesomeIcon icon={row.icon} color={row.iconColor} />
              {row.text}
            </Typography>

            {typeAlerts.map((a, idx) => {
              const open = openAlerts[a.id] || false;

              return (
                <Box key={idx} className={classes.alertBox}>
                  {/* Visa alltid meddelandet */}
                  <Typography className={classes.messageText}>{a.message}</Typography>

                  {/* Läs mer knapp */}
                  <Button
                    size="small"
                    onClick={() => setOpenAlerts(prev => ({ ...prev, [a.id]: !prev[a.id] }))}
                    sx={{ mt: 1 }}
                  >
                    {open ? "Visa mindre" : "Läs mer"}
                  </Button>

                  {/* Expandera detaljer */}
                  <Collapse in={open}>
                    <Box mt={1}>
                      <Typography className={classes.valueText}>
                        Under perioden <strong>{getDateToString(getDateTimeFromAPIDate(a.startDate))}</strong> till <strong>{getDateToString(getDateTimeFromAPIDate(a.endDate))}</strong> har genomsnittsvärdet legat på {formatters[row.formatter](a.averageValue?.toFixed(2))}, vilket är högre än det automatiskt framräknade tröskelvärdet {formatters[row.formatter](a.thresholdValue?.toFixed(2))}.
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            <Divider sx={{ my: 2 }} />
          </Box>
        );
      })}
    </Card>
  );
}

ConsumptionAlertsCard.propTypes = {
  className: PropTypes.string,
  alerts: PropTypes.array.isRequired,
};

export default ConsumptionAlertsCard;
